/**
 * One-off campaigns store their audience as `[{ type, id }]` entries, where `type` is either
 * `Label` (contacts carrying that label) or `Segment` (contacts matching a saved contact filter).
 *
 * The audience picker is a single flat multi-select, so an option value has to carry both facts.
 * `<type>:<id>` is that encoding — `buildAudienceOptions` writes it, `buildAudiencePayload` decodes
 * it back into the entries the API expects.
 */

export const AUDIENCE_TYPES = {
  LABEL: 'Label',
  SEGMENT: 'Segment',
};

const audienceOptionValue = (type, id) => `${type}:${id}`;

export const buildAudienceOptions = ({
  labels = [],
  segments = [],
  formatLabel = name => name,
  formatSegment = name => name,
} = {}) => [
  ...labels.map(label => ({
    value: audienceOptionValue(AUDIENCE_TYPES.LABEL, label.id),
    label: formatLabel(label.title),
  })),
  ...segments.map(segment => ({
    value: audienceOptionValue(AUDIENCE_TYPES.SEGMENT, segment.id),
    label: formatSegment(segment.name),
  })),
];

export const buildAudiencePayload = (values = []) =>
  values.map(value => {
    const [type, id] = String(value).split(':');
    return { type, id: Number(id) };
  });

/** The inverse of `buildAudiencePayload`: a saved audience back into picker values. */
export const buildAudienceValues = (audience = []) =>
  audience.map(({ type, id }) => audienceOptionValue(type, id));
